import type { SpecialtyRegionChip } from '../../api/types.js';
import {
  accentFor,
  color,
  font,
  fontSize,
  radius,
  space,
  tint,
  type UiRegion,
} from '../../theme/tokens.js';
import { Disclosure } from '../Disclosure/Disclosure.js';
import { provenanceFor, sourceFace } from '../Breakdown/provenance.js';
import { formatValue } from '../MetricCell/format.js';

/**
 * Six region chips, always all six (FR-S23).
 *
 * ⚠ **A region with no rows renders `—`, never `0`, and stays selectable.** The region exists;
 * the discipline is simply absent from it. Chips are a fixed navigation set, never a query
 * result — a region that vanished when it held nothing would take its own way back with it.
 *
 * With a discipline selected the figures become **that discipline's** per region (FR-S24). This is
 * the page's most useful single interaction — it answers *where is this discipline?* — and it
 * costs no round trip, because the row already carries its own `byRegion`.
 *
 * ## The chip shows the code and the figures, but not the name (owner decision, 2026-08-26)
 *
 * ⚠ A recorded departure from the blueprint, which puts `CODE Name — BoB (n TOA)` on the face.
 * The **name** goes, and so does the `TOA` word (2026-08-30): the name is the longest part of the
 * chip and the least informative, since the codes are already how a region is named everywhere
 * else in the product, and the word is a label for a figure that already has one in the panel.
 * Both come off so the chips fit one line instead of wrapping to two. The figures themselves,
 * and their labels, survive in the hover/focus panel and in the accessible name.
 *
 * The name still reaches the reader through the disclosure, which is routed via `<Disclosure>`
 * rather than a `title` attribute so it appears on **keyboard focus as well as hover** — a
 * `title` reaches a screen reader and leaves a keyboard user with no way to reveal anything
 * (FR-S34, FR-062).
 *
 * Everything else — the pill, its border, the accent-tinted selected state, the code badge — is
 * `<RegionPills>`'s treatment reused verbatim, so the two region strips in the product are the
 * same control with different payloads rather than two chips that merely resemble each other.
 */

export interface RegionStripProps {
  chips: SpecialtyRegionChip[];
  selected: UiRegion | null;
  /** `null` means **All** — every region, the page's default scope. */
  onSelect: (code: UiRegion | null) => void;
  /** Set when a discipline is selected, so the label says whose numbers these are. */
  scopeLabel?: string | null;
}

/** The shared rule, so a pill's count compacts on the same threshold as the table below it. */
const fmt = (value: number | null): string => formatValue(value);

/**
 * Sum across the six, `null` when every one of them is null.
 *
 * ⚠ Summed from the chips rather than taken from the response's `totals`, so **All** always
 * agrees with the six figures beside it. With a discipline selected the chips carry that
 * discipline's per-region numbers, and a total read from anywhere else would be the whole book's
 * — a row of six small numbers under a large unrelated one.
 */
function totalOf(chips: SpecialtyRegionChip[], key: 'bob' | 'activeToa'): number | null {
  const known = chips.map((c) => c[key]).filter((v): v is number => v != null);
  return known.length === 0 ? null : known.reduce((n, v) => n + v, 0);
}

/** Neutral by design: **All** is the absence of a region, so it borrows no region's accent. */
/*
 * The ALL chip's badge is `inkMuted` used as a FILL rather than as ink — the blueprint's own
 * `#5b6b86`, and the reason `onAccent` sits on grey here instead of on a region accent. Taking it
 * from the token is what fixes dark mode: the literal left near-black text on `#5b6b86` at 3.42:1,
 * where `inkMuted`'s dark value carries it at roughly 8:1.
 */
const ALL_ACCENT = color.inkMuted;

/**
 * The strip owns its inset and nothing else — no rule, no ground.
 *
 * It carried a `borderBottom` while it was a band *inside* the specialties card, separating it
 * from the table beneath. It now leads the page in a card of its own (Specialties §"1. Region
 * scope"), where that rule would read as a stray hairline above the card's own edge. The caller
 * supplies the chrome; this supplies the chips.
 */
export function RegionStrip({ chips, selected, onSelect, scopeLabel }: RegionStripProps) {
  return (
    <div
      role="group"
      aria-label={scopeLabel ? `Regions — ${scopeLabel}` : 'Regions'}
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: space['2xl'],
        padding: `${space['3xl']} ${space['5xl']}`,
      }}
    >
      <span
        style={{
          fontSize: fontSize.micro,
          fontWeight: 600,
          letterSpacing: '.5px',
          color: color.inkMuted,
          textTransform: 'uppercase',
        }}
      >
        Regions
      </span>

      <Chip
        label="ALL"
        accent={ALL_ACCENT}
        title="All regions"
        bob={totalOf(chips, 'bob')}
        activeToa={totalOf(chips, 'activeToa')}
        // Pressed when nothing is narrowed — the page's default scope made visible.
        active={selected === null}
        onClick={() => onSelect(null)}
      />

      {chips.map((chip) => (
        <Chip
          key={chip.code}
          label={chip.code}
          accent={accentFor(chip.code)}
          title={`${chip.code} · ${chip.name}`}
          name={chip.name}
          bob={chip.bob}
          activeToa={chip.activeToa}
          active={chip.code === selected}
          // Toggle: choosing the active chip clears back to all six (FR-S23).
          onClick={() => onSelect(chip.code)}
        />
      ))}
    </div>
  );
}

function Chip({
  label,
  accent,
  title,
  name,
  bob,
  activeToa,
  active,
  onClick,
}: {
  label: string;
  accent: string;
  title: string;
  name?: string;
  bob: number | null;
  activeToa: number | null;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <Disclosure
      // The name leads, since it is what the code stands for and no longer sits on the face.
      accessibleName={`${name ?? title} — ${fmt(bob)} book of business, ${fmt(activeToa)} on assignment`}
      panel={
        /*
         * ⚠ **One line naming BOTH figures, not the standard footer** (2026-09-10). This chip is
         * the one tooltip in the product carrying two figures from two systems — the book is
         * Nexus and the placements are Run Rate — so `<ProvenanceFootnote>`'s single `Source: …`
         * would have to name both and leave the reader to work out which was which, and two
         * stacked footers would be taller than the chip's own panel.
         *
         * No formula sentences either, for the same reason: both figures have a column of their
         * own in the table below, and that is where each one's is.
         */
        <span style={{ display: 'block', textAlign: 'left', whiteSpace: 'nowrap' }}>
          <span style={{ display: 'block', fontWeight: 700 }}>{title}</span>
          <span style={{ display: 'block', fontFamily: font.mono, marginTop: 3 }}>
            BoB: {fmt(bob)}
          </span>
          <span style={{ display: 'block', fontFamily: font.mono }}>TOA: {fmt(activeToa)}</span>
          <SourceLine />
        </span>
      }
      onClick={onClick}
      pressed={active}
      className="chip"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 7,
        // Identical to `<RegionPills>` on the Regions page: white pill, accent-tinted when
        // selected, accent border to match. A region button should not look like one thing here
        // and another there — the two strips sit one click apart.
        background: active ? tint(accent, 8) : color.surface,
        border: `1px solid ${active ? accent : color.borderStrong}`,
        borderRadius: radius.pill,
        // The blueprint's own `6px 13px 6px 10px` — the extra right padding pays for the figures
        // sitting to the badge's right.
        padding: '6px 13px 6px 10px',
        cursor: 'pointer',
        fontFamily: 'inherit',
      }}
    >
      <span
        className="mono"
        style={{
          fontFamily: font.mono,
          fontSize: '10px',
          fontWeight: 600,
          color: color.onAccent,
          background: accent,
          padding: '1px 6px',
          borderRadius: radius.sm,
        }}
      >
        {label}
      </span>
      {/*
       * The blueprint's `— BoB (n TOA)`, minus its leading separator dash AND the `TOA` word.
       * The dash divided the code from the region NAME, and with the name gone it would sit next
       * to the em dash an empty region renders, reading as `— — (— TOA)`. The word goes for
       * the same reason the name did — the chips have to fit one line — and it costs nothing,
       * because the hover panel and the accessible name both still spell both figures out.
       */}
      <span style={{ fontFamily: font.mono, fontSize: '11px', color: color.inkFaint }}>
        {fmt(bob)} ({fmt(activeToa)})
      </span>
    </Disclosure>
  );
}

/**
 * Which system each of the chip's two figures came from, on one line.
 *
 * The names are read out of the shared map rather than written here, so a figure that moves
 * between systems moves in one place — which is the whole reason `provenance.ts` exists.
 */
function SourceLine() {
  const bob = provenanceFor('bob');
  const toa = provenanceFor('activeToa');
  if (!bob || !toa) return null;

  return (
    <span
      style={{
        display: 'block',
        marginTop: 5,
        paddingTop: 4,
        borderTop: `1px solid ${color.tipBorderInner}`,
        fontSize: fontSize.micro,
        fontWeight: 600,
        letterSpacing: '.2px',
        color: color.tipInk,
      }}
    >
      {`Source: BoB ${sourceFace(bob.sources)} · TOA ${sourceFace(toa.sources)}`}
    </span>
  );
}
